/**
 * Phantom Embedded Wallet Context
 *
 * Wraps Phantom's official React SDK so the rest of the app can continue to
 * interact with a simple context API. The React SDK handles the Google based
 * authentication flow, modal controls, and exposes Solana providers for
 * signing.
 */

import { createContext, useContext, ReactNode, useMemo, useCallback, useEffect } from 'react';
import { PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js';
import {
  AddressType,
  PhantomProvider as PhantomReactProvider,
  useAccounts,
  useConnect,
  useDisconnect,
  useModal,
  usePhantom as usePhantomSDK,
  useSolana,
} from '@phantom/react-sdk';

import config from '@/config';
import { logger } from '@/lib/logger';

interface SignAndSendResult {
  signature: string;
}

interface PhantomContextType {
  isPhantomAvailable: boolean;
  isPhantomLoading: boolean;
  isPhantomConnected: boolean;
  isPhantomVisible: boolean;
  isEmbeddedWallet: boolean;
  phantomError: string | null;
  phantomAddress: string | null;
  phantomPublicKey: PublicKey | null;
  connectPhantom: () => Promise<void>;
  disconnectPhantom: () => Promise<void>;
  showPhantom: () => void;
  hidePhantom: () => void;
  signPhantomMessage: (message: Uint8Array) => Promise<Uint8Array>;
  signPhantomTransaction: <T extends Transaction | VersionedTransaction>(transaction: T) => Promise<T>;
  signAllPhantomTransactions: <T extends Transaction | VersionedTransaction>(transactions: T[]) => Promise<T[]>;
  signAndSendPhantomTransaction: (transaction: VersionedTransaction) => Promise<SignAndSendResult>;
}

const PhantomContext = createContext<PhantomContextType | undefined>(undefined);
const DISABLED_MESSAGE = 'Phantom embedded wallet is disabled in this environment';

const disabledAction = async () => {
  throw new Error(DISABLED_MESSAGE);
};

const disabledContextValue: PhantomContextType = {
  isPhantomAvailable: false,
  isPhantomLoading: false,
  isPhantomConnected: false,
  isPhantomVisible: false,
  isEmbeddedWallet: false,
  phantomError: null,
  phantomAddress: null,
  phantomPublicKey: null,
  connectPhantom: disabledAction,
  disconnectPhantom: async () => {},
  showPhantom: () => {},
  hidePhantom: () => {},
  signPhantomMessage: disabledAction,
  signPhantomTransaction: async <T extends Transaction | VersionedTransaction>(): Promise<T> => {
    throw new Error(DISABLED_MESSAGE);
  },
  signAllPhantomTransactions: async <T extends Transaction | VersionedTransaction>(): Promise<T[]> => {
    throw new Error(DISABLED_MESSAGE);
  },
  signAndSendPhantomTransaction: async (): Promise<SignAndSendResult> => {
    throw new Error(DISABLED_MESSAGE);
  },
};

function PhantomContextManager({ children }: { children: ReactNode }) {
  const phantomState = usePhantomSDK();
  const { isLoading: isConnectLoading, error: connectError } = useConnect();
  const { disconnect } = useDisconnect();
  const { solana } = useSolana();
  const accounts = useAccounts();
  const { open, close, isOpened } = useModal();

  const solanaAccount = useMemo(() => {
    const account = accounts?.find((account) => account.addressType === AddressType.solana) ?? null;
    if (account) {
      logger.debug('[PhantomContext] Solana account found:', account.address);
    } else {
      logger.debug('[PhantomContext] No Solana account found in accounts:', accounts);
    }
    return account;
  }, [accounts]);

  const phantomAddress = solanaAccount?.address ?? null;

  const phantomPublicKey = useMemo(() => {
    if (!phantomAddress) {
      return null;
    }

    try {
      const pubKey = new PublicKey(phantomAddress);
      logger.debug('[PhantomContext] Phantom public key created:', phantomAddress);
      return pubKey;
    } catch (error) {
      logger.error('[PhantomContext] Invalid Phantom public key:', error);
      return null;
    }
  }, [phantomAddress]);

  const isPhantomAvailable = Boolean(phantomState.sdk);
  const isPhantomLoading = phantomState.isLoading || isConnectLoading;

  // Detect embedded wallet: check authProvider - "injected" means extension, anything else is OAuth
  // authProvider can be: "google" | "apple" | "phantom" | "tiktok" | "x" | "device" | "injected"
  const isEmbeddedWallet = useMemo(() => {
    // If not connected, not an embedded wallet
    if (!phantomState.isConnected) return false;

    // Check the authProvider from the user/connect result
    // "injected" = browser extension
    // "google", "apple", "phantom", "tiktok", "x", "device" = embedded/OAuth wallets
    const authProvider = phantomState.user?.authProvider;
    const isEmbedded = authProvider !== undefined && authProvider !== 'injected';

    if (isEmbedded) {
      logger.debug('[PhantomContext] Detected embedded wallet (OAuth-based), authProvider:', authProvider);
    } else if (phantomState.isConnected) {
      logger.debug('[PhantomContext] Connected via extension, authProvider:', authProvider);
    }

    return isEmbedded;
  }, [phantomState.isConnected, phantomState.user?.authProvider]);

  const suppressedErrors = ['no valid session found', 'no wallet found'];
  const rawErrorMessage = connectError?.message ?? phantomState.connectError?.message ?? null;
  const phantomError =
    rawErrorMessage && !suppressedErrors.some((msg) => rawErrorMessage.toLowerCase().includes(msg))
      ? rawErrorMessage
      : null;

  const connectPhantom = useCallback(async () => {
    if (!isPhantomAvailable) {
      throw new Error('Phantom embedded wallet is not available');
    }

    logger.info('[PhantomContext] Opening Phantom connection modal');
    // Open the modal to let user choose provider
    open();
    // The actual connection will happen when user selects a provider in the modal
  }, [isPhantomAvailable, open]);

  const disconnectPhantom = useCallback(async () => {
    logger.info('[PhantomContext] Disconnecting Phantom wallet');
    await disconnect();
    close();
  }, [disconnect, close]);

  const showPhantom = useCallback(() => {
    open();
  }, [open]);

  const hidePhantom = useCallback(() => {
    close();
  }, [close]);

  const ensureSolanaProvider = useCallback(() => {
    if (!solana) {
      throw new Error('Phantom Solana provider unavailable');
    }
    return solana;
  }, [solana]);

  // Switch to devnet in development mode
  useEffect(() => {
    if (solana && config.IS_DEV && phantomState.isConnected) {
      const switchToDevnet = async () => {
        try {
          // Check if switchNetwork is available
          if (typeof solana.switchNetwork === 'function') {
            logger.info('[PhantomContext] Switching to devnet for development');
            await solana.switchNetwork('devnet');
            logger.info('[PhantomContext] Successfully switched to devnet');
          } else {
            logger.warn('[PhantomContext] switchNetwork not available on Solana provider');
          }
        } catch (error) {
          logger.error('[PhantomContext] Failed to switch to devnet:', error);
        }
      };
      switchToDevnet();
    }
  }, [solana, phantomState.isConnected]);

  const signPhantomMessage = useCallback(
    async (message: Uint8Array): Promise<Uint8Array> => {
      if (!phantomState.isConnected) {
        throw new Error('Phantom wallet is not connected');
      }

      const solanaProvider = ensureSolanaProvider();
      const { signature } = await solanaProvider.signMessage(message);
      logger.info('[PhantomContext] Message signed with Phantom');
      return signature;
    },
    [ensureSolanaProvider, phantomState.isConnected]
  );

  const signPhantomTransaction = useCallback(
    async <T extends Transaction | VersionedTransaction>(transaction: T): Promise<T> => {
      if (!phantomState.isConnected) {
        throw new Error('Phantom wallet is not connected');
      }

      const solanaProvider = ensureSolanaProvider();

      // Note: signTransaction may not be supported for embedded wallets
      // Consider using signAndSendTransaction for actual transactions
      logger.warn('[PhantomContext] Using signTransaction - this may not work with embedded wallets');

      try {
        const signedTx = (await solanaProvider.signTransaction(transaction)) as T;
        logger.info('[PhantomContext] Transaction signed with Phantom');
        return signedTx;
      } catch (error) {
        logger.error('[PhantomContext] Failed to sign transaction - embedded wallets may not support this method:', error);
        throw new Error('Transaction signing failed. Embedded wallets should use signAndSendTransaction instead.');
      }
    },
    [ensureSolanaProvider, phantomState.isConnected]
  );

  const signAllPhantomTransactions = useCallback(
    async <T extends Transaction | VersionedTransaction>(transactions: T[]): Promise<T[]> => {
      if (!phantomState.isConnected) {
        throw new Error('Phantom wallet is not connected');
      }

      const solanaProvider = ensureSolanaProvider();

      // Note: signAllTransactions may not be supported for embedded wallets
      logger.warn('[PhantomContext] Using signAllTransactions - this may not work with embedded wallets');

      try {
        const signedTxs = (await solanaProvider.signAllTransactions(transactions)) as T[];
        logger.info('[PhantomContext] Batch transactions signed with Phantom:', signedTxs.length);
        return signedTxs;
      } catch (error) {
        logger.error('[PhantomContext] Failed to sign batch transactions - embedded wallets may not support this method:', error);
        throw new Error('Batch transaction signing failed. Embedded wallets should use signAndSendTransaction instead.');
      }
    },
    [ensureSolanaProvider, phantomState.isConnected]
  );

  // signAndSendTransaction - Required for embedded wallets (OAuth-based)
  // This method signs and broadcasts the transaction atomically
  const signAndSendPhantomTransaction = useCallback(
    async (transaction: VersionedTransaction): Promise<SignAndSendResult> => {
      if (!phantomState.isConnected) {
        throw new Error('Phantom wallet is not connected');
      }

      const solanaProvider = ensureSolanaProvider();

      logger.info('[PhantomContext] Using signAndSendTransaction (embedded wallet compatible)');

      try {
        const result = await solanaProvider.signAndSendTransaction(transaction);
        logger.info('[PhantomContext] Transaction signed and sent:', result.signature);
        return { signature: result.signature };
      } catch (error: any) {
        logger.error('[PhantomContext] Failed to sign and send transaction:', error);

        const errorMessage = error?.message || String(error);

        // Handle spending limit errors gracefully
        if (errorMessage.includes('spending limit') || error?.code === 'SPENDING_LIMIT_EXCEEDED') {
          throw new Error(
            'This transaction exceeds your spending limit. ' +
            'Please update your spending limit in Phantom settings (Settings > Connected Apps > Junk.Fun) to continue.'
          );
        }

        // Handle 403 prepare errors (likely Portal configuration issue)
        if (errorMessage.includes('403') && errorMessage.includes('prepare')) {
          logger.error('[PhantomContext] Phantom Portal configuration issue - app may not be properly registered');
          throw new Error(
            'Unable to process transaction with embedded wallet. ' +
            'Please try using the Phantom browser extension, or contact support if the issue persists.'
          );
        }

        throw error;
      }
    },
    [ensureSolanaProvider, phantomState.isConnected]
  );

  return (
    <PhantomContext.Provider
      value={{
        isPhantomAvailable,
        isPhantomLoading,
        isPhantomConnected: phantomState.isConnected,
        isPhantomVisible: isOpened,
        isEmbeddedWallet,
        phantomError,
        phantomAddress,
        phantomPublicKey,
        connectPhantom,
        disconnectPhantom,
        showPhantom,
        hidePhantom,
        signPhantomMessage,
        signPhantomTransaction,
        signAllPhantomTransactions,
        signAndSendPhantomTransaction,
      }}
    >
      {children}
    </PhantomContext.Provider>
  );
}

export function PhantomProvider({ children }: { children: ReactNode }) {
  // Custom theme matching Junk.Fun's dark theme with cyan accents
  const theme = useMemo(
    () => ({
      background: '#1B1B1B' as const,     // Main app background
      secondary: '#51E4F8' as const,      // Cyan for borders, dividers, secondary elements
      brand: '#50E4F9' as const,          // Cyan for primary buttons and actions
      text: '#FFFFFF' as const,           // White text
      error: '#FF4444' as const,          // Error states
      success: '#4ADE80' as const,        // Success states
      borderRadius: '16px',               // Rounded corners matching app style
      overlay: 'rgba(0, 0, 0, 0.75)',     // Modal backdrop
    }),
    []
  );

  if (!config.ENABLE_PHANTOM_EMBEDDED_WALLET) {
    return <PhantomContext.Provider value={disabledContextValue}>{children}</PhantomContext.Provider>;
  }

  const redirectUrl =
    typeof window !== 'undefined' ? new URL('/', window.location.origin).toString() : undefined;

  return (
    <PhantomReactProvider
      config={{
        providers: ['google', 'apple', 'injected'],
        addressTypes: [AddressType.solana],
        appId: config.PHANTOM_APP_ID,
        authOptions: redirectUrl
          ? {
              authUrl: 'https://connect.phantom.app/login',
              redirectUrl: redirectUrl,
            }
          : {
              authUrl: 'https://connect.phantom.app/login',
            },
      }}
      theme={theme}
      appIcon="https://www.junk.fun/logo.png"
      appName="Junk.Fun"
    >
      <PhantomContextManager>{children}</PhantomContextManager>
    </PhantomReactProvider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePhantom() {
  const context = useContext(PhantomContext);
  if (!context) {
    throw new Error('usePhantom must be used within PhantomProvider');
  }
  return context;
}

// Export a hook to check if we're using Phantom (for AuthContext)
// eslint-disable-next-line react-refresh/only-export-components
export function useIsPhantomActive() {
  const context = useContext(PhantomContext);
  return context?.isPhantomConnected || false;
}
